import React, { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useNavigationHistory } from './hooks/useNavigationHistory';
import { useMotionPolicy } from './hooks/useMotionPolicy';
import { useExperiencePrefs } from './hooks/useExperiencePrefs';
import { effectivePrefsView } from './prefs/effectiveView';
import { useFocusOnRouteChange } from './hooks/useFocusOnRouteChange';
import { resolveParentEntry } from './lib/parentMap';
import { parentMapTLA } from './config/parentMap.tla';
import { reduceAnnouncement } from './lib/ariaLive';
import { createInertEventBus } from './lib/eventContract';

// The reusable Experience substrate provider (arch doc §3.9/§4.8/D1/D18).
// Mounts INSIDE BrowserRouter (needs useLocation). Owns: the in-app navigation history,
// the SINGLE polite aria-live region + announce() with navigation-over-celebration
// precedence, the motion policy, and an INERT event bus (no producers in Wave 1).
const ExperienceContext = createContext(null);

export function ExperienceProvider({ children }) {
  const { pathname } = useLocation();
  const { t } = useLanguage();

  // Best-effort translated label for the current route (used for "Back to X").
  const entry = resolveParentEntry(parentMapTLA, pathname);
  const currentLabel = t(entry.labelKey, entry.labelKey);

  const history = useNavigationHistory(pathname, currentLabel);

  // Wave 2: user preferences drive the motion policy (quietMode applied via effectivePrefsView).
  // The hook is the ONLY conduit by which prefs reach the resolver (D5).
  const { prefs, updatePrefs } = useExperiencePrefs();
  const eff = effectivePrefsView(prefs);
  const motionPolicy = useMotionPolicy({ reducedMotion: eff.reducedMotion, mode: eff.mode });

  const eventBus = useMemo(() => createInertEventBus(), []);

  const [announcement, setAnnouncement] = useState(null);
  const announce = useCallback((kind, text) => {
    // sameTick guards the D18 rule: a celebration never clobbers a navigation announcement.
    setAnnouncement((prev) => reduceAnnouncement(prev, { kind, text, sameTick: true }));
  }, []);

  useFocusOnRouteChange(announce);

  const value = useMemo(
    () => ({ history, motionPolicy, eventBus, announce, prefs, updatePrefs }),
    [history, motionPolicy, eventBus, announce, prefs, updatePrefs]
  );

  return (
    <ExperienceContext.Provider value={value}>
      {children}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement ? announcement.text : ''}
      </div>
    </ExperienceContext.Provider>
  );
}

export function useExperience() {
  const ctx = useContext(ExperienceContext);
  if (!ctx) throw new Error('useExperience must be used within an ExperienceProvider');
  return ctx;
}
